import {
  type FC,
  createContext,
  useContext,
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  ThreadPrimitive,
  useThread,
  ActionBarPrimitive,
  ComposerPrimitive,
  MessagePrimitive,
  useMessage,
  type ToolCallMessagePartProps,
} from "@assistant-ui/react";
import { ArrowDown } from "lucide-react";
import { ChatGreeting } from "./ChatGreeting";
import { InProgressSpinner } from "../shared/InProgressSpinner";
import { useFintheonAgents } from "../../contexts/FintheonAgentContext";
import { ToolApprovalCard } from "./ToolApprovalCard";
import {
  useToolApprovals,
  type AddToolApprovalResponse,
} from "./hooks/useToolApprovals";
import { AssistantMessagePrimitive } from "./AssistantMessagePrimitive";
import { UserMessagePrimitive } from "./UserMessagePrimitive";
import { ChatInlineActionWidgets } from "./ChatInlineActionWidgets";
import { FintheonThinkingIndicator } from "./FintheonThinkingIndicator";
import { ChatErrorBanner } from "./ChatErrorBanner";
import type { Citation } from "./CitationChip";
import type {
  ChatAnswerWidget,
  ChatUiPlanProposal,
} from "./hooks/useChatUiActions";

function extractText(msg: any): string {
  const parts = msg.content ?? msg.parts ?? [];
  if (!Array.isArray(parts)) return typeof parts === "string" ? parts : "";
  return parts
    .filter((p: any) => p.type === "text" && typeof p.text === "string")
    .map((p: any) => p.text)
    .join("\n\n");
}

function extractRawContent(msg: any): any {
  return msg.parts ?? msg.content;
}

function hasVisibleAssistantText(msg: any): boolean {
  return extractText(msg).trim().length > 0;
}

function hasAssistantTextAfterLastUser(messages: readonly any[]): boolean {
  const lastUserIndex = messages.map((msg) => msg.role).lastIndexOf("user");
  if (lastUserIndex < 0) return false;
  return messages
    .slice(lastUserIndex + 1)
    .some((msg) => msg.role === "assistant" && hasVisibleAssistantText(msg));
}

const ScrollToBottomButton: FC<{
  containerRef: RefObject<HTMLElement | null>;
  bottomOffset?: number;
}> = ({ containerRef, bottomOffset = 116 }) => {
  const [showButton, setShowButton] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => setShowButton(!entry.isIntersecting),
      {
        root: containerRef.current,
        threshold: 0.1,
        rootMargin: "80px 0px 0px 0px",
      },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [containerRef]);

  const scrollToBottom = useCallback(() => {
    const reduced =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    containerRef.current?.scrollTo({
      top: containerRef.current.scrollHeight,
      behavior: reduced ? "auto" : "smooth",
    });
  }, [containerRef]);

  return (
    <>
      <div ref={sentinelRef} className="h-1 w-full" />
      {showButton && (
        <button
          onClick={scrollToBottom}
          className="scroll-to-bottom-btn fixed left-1/2 z-30 flex -translate-x-1/2 items-center justify-center rounded-full text-[var(--fintheon-accent)] hover:bg-[var(--fintheon-accent)]/10"
          style={{
            width: "36px",
            height: "36px",
            bottom: `${bottomOffset}px`,
            background:
              "color-mix(in srgb, var(--fintheon-accent) 9%, rgba(5,4,2,0.78))",
            border:
              "1px solid color-mix(in srgb, var(--fintheon-accent) 22%, transparent)",
            backdropFilter: "blur(18px)",
            WebkitBackdropFilter: "blur(18px)",
          }}
          title="Scroll to bottom"
        >
          <ArrowDown size={16} />
        </button>
      )}
    </>
  );
};

export const AiLoader: FC = () => (
  <div className="flex items-center justify-center py-3">
    <InProgressSpinner size={16} label="Loading conversation" />
  </div>
);

interface FintheonThreadProps {
  onSend: (msg: string) => void;
  isLoading: boolean;
  agentName?: string;
  onTakeNote?: (messageId: string, content: string) => void;
  lastError?: string | null;
  lastRequestId?: string | null;
  compact?: boolean;
  hasSubmittedMessage?: boolean;
  citations?: Citation[];
  onPinCitation?: (citation: Citation) => void;
  pinnedCitationIndex?: number;
  scrollButtonOffset?: number;
  composerBottomInset?: number;
  showThoughtTrail?: boolean;
  thinkingContent?: string;
  thinkingLabel?: string;
  answerWidgets?: ChatAnswerWidget[];
  planProposal?: ChatUiPlanProposal | null;
  onPlanDisplayComplete?: (actionId: string) => void;
  emptyState?: ReactNode;
  addToolApprovalResponse?: AddToolApprovalResponse;
}

interface MessagePresentation {
  agentName: string;
  citations: Citation[];
  onPinCitation?: (citation: Citation) => void;
  pinnedCitationIndex?: number;
  onTakeNote?: (messageId: string, content: string) => void;
}

const MessagePresentationContext = createContext<MessagePresentation | null>(
  null,
);

function PrimitiveUserMessage() {
  const message = useMessage();
  return (
    <MessagePrimitive.Root className="group relative">
      <UserMessagePrimitive
        rawContent={message.content}
        createdAt={message.createdAt}
      />
      <ActionBarPrimitive.Root
        autohide="always"
        className="mt-1 flex justify-end gap-1 text-[10px] text-[var(--fintheon-muted)]"
      >
        <ActionBarPrimitive.Copy className={messageActionClass}>
          Copy
        </ActionBarPrimitive.Copy>
        <ActionBarPrimitive.Edit className={messageActionClass}>
          Edit
        </ActionBarPrimitive.Edit>
      </ActionBarPrimitive.Root>
    </MessagePrimitive.Root>
  );
}

function PrimitiveAssistantMessage() {
  const message = useMessage();
  const presentation = useContext(MessagePresentationContext);
  if (!presentation || !hasRenderableAssistantParts(message.content)) {
    return null;
  }
  const text = extractText(message);
  return (
    <MessagePrimitive.Root className="group relative">
      <AssistantMessagePrimitive
        rawContent={message.content}
        messageId={message.id}
        agentName={presentation.agentName}
        genTime={message.createdAt}
        citations={presentation.citations}
        onPinCitation={presentation.onPinCitation}
        pinnedCitationIndex={presentation.pinnedCitationIndex}
        onTakeNote={presentation.onTakeNote}
        isStreaming={message.status?.type === "running"}
        typedParts={
          <MessagePrimitive.Parts
            components={{
              Text: HiddenMessagePart,
              Reasoning: HiddenMessagePart,
              tools: { Fallback: FintheonToolPart },
            }}
          />
        }
        actions={
          <ActionBarPrimitive.Root
            autohide="always"
            className="mt-2 flex gap-1 text-[10px] text-[var(--fintheon-muted)]"
          >
            <ActionBarPrimitive.Copy className={messageActionClass}>
              Copy
            </ActionBarPrimitive.Copy>
            <button
              type="button"
              className={messageActionClass}
              onClick={() => publishFollowUpReference(message.id, text)}
            >
              Ask follow-up
            </button>
          </ActionBarPrimitive.Root>
        }
      />
    </MessagePrimitive.Root>
  );
}

function PrimitiveUserEditComposer() {
  return (
    <ComposerPrimitive.Root className="ml-auto flex max-w-[85%] flex-col gap-2 rounded-lg border border-[color-mix(in_srgb,var(--fintheon-accent)_22%,transparent)] bg-[#070603] p-2">
      <ComposerPrimitive.Input className="min-h-16 resize-none bg-transparent text-[13px] text-[var(--fintheon-text)] outline-none" />
      <div className="flex justify-end gap-1">
        <ComposerPrimitive.Cancel className={messageActionClass}>
          Cancel
        </ComposerPrimitive.Cancel>
        <ComposerPrimitive.Send className={messageActionClass}>
          Save and resend
        </ComposerPrimitive.Send>
      </div>
    </ComposerPrimitive.Root>
  );
}

function HiddenMessagePart() {
  return null;
}

function FintheonToolPart(props: ToolCallMessagePartProps) {
  const detail = props.result === undefined ? props.args : props.result;
  const state =
    props.status.type === "running"
      ? "Working"
      : props.isError
        ? "Needs attention"
        : props.result === undefined
          ? "Ready"
          : "Done";
  return (
    <details className="mt-2 rounded-md border border-[color-mix(in_srgb,var(--fintheon-accent)_16%,transparent)] bg-[#050402] px-2.5 py-2 text-[11px]">
      <summary className="cursor-pointer list-none text-[var(--fintheon-accent)]">
        {humanizeToolName(props.toolName)} · {state}
      </summary>
      <pre className="mt-2 max-h-44 overflow-auto whitespace-pre-wrap break-words text-[10px] leading-4 text-[var(--fintheon-muted)]">
        {safeJson(detail)}
      </pre>
    </details>
  );
}

const messageActionClass =
  "rounded px-1.5 py-1 text-[10px] text-[var(--fintheon-muted)] transition-colors hover:bg-[color-mix(in_srgb,var(--fintheon-accent)_8%,transparent)] hover:text-[var(--fintheon-accent)]";

export function FintheonThread({
  onSend,
  isLoading,
  agentName,
  onTakeNote,
  lastError,
  lastRequestId,
  compact,
  hasSubmittedMessage,
  citations = [],
  onPinCitation,
  pinnedCitationIndex,
  scrollButtonOffset,
  composerBottomInset = 136,
  showThoughtTrail,
  thinkingContent,
  thinkingLabel,
  answerWidgets = [],
  planProposal = null,
  onPlanDisplayComplete,
  emptyState,
  addToolApprovalResponse,
}: FintheonThreadProps) {
  const { activeAgent } = useFintheonAgents();
  const viewportRef = useRef<HTMLDivElement>(null);
  const { approvals, sendDecision } = useToolApprovals(
    lastRequestId ?? null,
    addToolApprovalResponse,
  );
  const messages = useThread((s) => s.messages);

  const resolvedAgent = agentName ?? activeAgent?.name ?? "Harper";
  const hasThinkingContent = Boolean(thinkingContent?.trim());
  const hasPendingPlanApproval = planProposal?.status === "pending";
  const shouldShowThoughtTrail =
    !hasPendingPlanApproval &&
    (showThoughtTrail ?? isLoading) &&
    (hasThinkingContent || !hasAssistantTextAfterLastUser(messages));

  return (
    <MessagePresentationContext.Provider
      value={{
        agentName: resolvedAgent,
        citations,
        onPinCitation,
        pinnedCitationIndex,
        onTakeNote,
      }}
    >
      <ThreadPrimitive.Root className="flex-1 flex flex-col min-h-0 relative">
        <ThreadPrimitive.Viewport
          ref={viewportRef as any}
          className="min-h-0 flex-1 overflow-y-auto px-6 pb-8 pt-12 md:p-6 md:pb-8"
          style={{ paddingBottom: composerBottomInset }}
        >
          <div className="max-w-full mx-auto space-y-4 mb-8">
            {messages.length === 0 &&
              !isLoading &&
              !hasSubmittedMessage &&
              emptyState}
            {!emptyState &&
              !compact &&
              messages.length === 0 &&
              !isLoading &&
              !hasSubmittedMessage && (
                <ChatGreeting onSend={onSend} isLoading={isLoading} />
              )}
            {!emptyState &&
              compact &&
              messages.length === 0 &&
              !hasSubmittedMessage && (
                <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                  <p className="text-sm text-[var(--fintheon-accent)]/60 font-medium">
                    Ave, Trader.
                  </p>
                  <p className="text-[11px] text-zinc-600 mt-1">
                    Quick dispatch from the War Room.
                  </p>
                </div>
              )}
            {messages.length === 0 && hasSubmittedMessage ? <AiLoader /> : null}

            <ThreadPrimitive.Messages
              components={{
                UserMessage: PrimitiveUserMessage,
                UserEditComposer: PrimitiveUserEditComposer,
                AssistantMessage: PrimitiveAssistantMessage,
              }}
            />

            {approvals.map((a) => (
              <ToolApprovalCard
                key={a.approvalId}
                approval={a}
                onApprove={(id) => sendDecision(id, "approved")}
                onDeny={(id) => sendDecision(id, "denied")}
              />
            ))}

            <ChatInlineActionWidgets
              answerWidgets={answerWidgets}
              planProposal={planProposal}
              onPlanDisplayComplete={onPlanDisplayComplete}
            />
            {shouldShowThoughtTrail ? (
              <div className="flex justify-start">
                <FintheonThinkingIndicator
                  isThinking
                  agentName={resolvedAgent}
                  thinkingContent={thinkingContent}
                  phraseOverride={thinkingLabel}
                  orbActivity="composing"
                />
              </div>
            ) : null}

            {lastError && !isLoading && <ChatErrorBanner error={lastError} />}

            <ScrollToBottomButton
              containerRef={viewportRef}
              bottomOffset={scrollButtonOffset}
            />
          </div>
        </ThreadPrimitive.Viewport>
      </ThreadPrimitive.Root>
    </MessagePresentationContext.Provider>
  );
}

function hasRenderableAssistantParts(content: readonly any[]): boolean {
  return content.some(
    (part) =>
      (part.type === "text" && String(part.text ?? "").trim()) ||
      part.type === "tool-call" ||
      part.type === "source" ||
      part.type === "file" ||
      part.type === "image",
  );
}

function publishFollowUpReference(messageId: string, text: string) {
  window.dispatchEvent(
    new CustomEvent("fintheon:chat-follow-up-reference", {
      detail: { messageId, text: text.slice(0, 280) },
    }),
  );
  window.dispatchEvent(new CustomEvent("fintheon:chat-composer-focus"));
}

function humanizeToolName(value: string): string {
  return value
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function safeJson(value: unknown): string {
  try {
    return JSON.stringify(value ?? {}, null, 2);
  } catch {
    return "Tool detail is unavailable.";
  }
}
